import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import {
  useLazyGetResidentDetailsQuery,
  useLazyGetResidentPaymentInfoQuery,
  useLazyGetResidentTransactionInfoQuery,
} from '@/store/api';
import { handleApiError, EnhancedError } from '@/utils/admin/errorHandling';
import { IResidentDetailsParams, IResidentDetailsResponse } from '@/types/admin/residentDetails';

export const useResidentDetails = ({ accountId, leaseId, mode }: IResidentDetailsParams) => {
  const [getDetails, detailsState] = useLazyGetResidentDetailsQuery();
  const [getPayment, paymentState] = useLazyGetResidentPaymentInfoQuery();
  const [getTransactions, txState] = useLazyGetResidentTransactionInfoQuery();

  // Error handling state
  const [enhancedError, setEnhancedError] = useState<EnhancedError | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);

  // Track if component is mounted to prevent memory leaks
  const isMounted = useRef(true);

  // Fetch all four endpoints in parallel
  const fetchResidentDetails = useCallback(async () => {
    if (!accountId || !leaseId) {
      console.warn('Missing accountId or leaseId, cannot fetch resident details');
      return;
    }

    // Clear previous error
    setEnhancedError(null);

    try {
      await Promise.all([
        getDetails({ accountId, leaseId, mode }).unwrap(),
        getPayment({ accountId, leaseId }).unwrap().catch(() => null),
        getTransactions({ accountId, leaseId }).unwrap().catch(() => null),
      ]);
      // Reset retry count on success
      if (isMounted.current) {
        setRetryCount(0);
      }
    } catch (fetchError) {
      // Only update state if component is still mounted
      if (isMounted.current) {
        const errorDetails = handleApiError(fetchError, '/api/v1/residents/details', retryCount);
        setEnhancedError(errorDetails);
        setRetryCount((prev) => prev + 1);

        console.error('🔴 Failed to fetch resident details after retries:', errorDetails);
      }
    }
  }, [accountId, leaseId, mode, getDetails, getPayment, getTransactions, retryCount]);

  // Trigger fetch when component mounts or params change
  useEffect(() => {
    isMounted.current = true;
    fetchResidentDetails();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted.current = false;
    };
  }, [accountId, leaseId, mode, fetchResidentDetails]);

  // Process and merge resident details data from all four endpoints
  const residentDetails: IResidentDetailsResponse | null = useMemo(() => {
    const d = detailsState.data;
    if (!d) return null;

    // Find the most recent payment from transaction history
    const tx = (txState.data as any)?.transactions ?? [];
    const lastPayment = tx
      .filter((t: any) => (t.type ?? t.transactionType) === 'payment')
      .sort(
        (a: any, b: any) =>
          new Date(b.transactionDate ?? 0).getTime() - new Date(a.transactionDate ?? 0).getTime()
      )[0];

    return {
      // Basic info
      accountId: d.accountId,
      leaseId, // Use the parameter, not data.leaseId
      firstName: d.firstName,
      lastName: d.lastName,
      email: d.email,
      phoneNumber: d.phoneNumber,
      customerType: d.customerType,

      // Lease info
      leaseStatus: d.leaseStatus,
      propertyName: d.propertyName,
      buildingUnitNumberSpace: (d as any).unitNumber ?? d.buildingUnitNumberSpace,
      startDate: (d as any).startDate,
      endDate: (d as any).endDate,
      moveInDate: (d as any).moveInDate,
      moveOutDate: (d as any).moveOutDate,

      // Payment info (from useLazyGetResidentPaymentInfoQuery)
      // Backend serializes balance from a Decimal — coerce to a finite number
      // so consumers can safely call toFixed/compare numerically.
      balanceDue: (() => {
        const raw = (paymentState.data as any)?.balance;
        if (raw == null) return undefined;
        const n = typeof raw === 'number' ? raw : Number(raw);
        return Number.isFinite(n) ? n : undefined;
      })(),
      hasAutopay: (paymentState.data as any)?.hasAutopay,
      paymentsBlocked: (paymentState.data as any)?.paymentsBlocked,
      paymentSources: (paymentState.data as any)?.paymentSources ?? [],
      recurringPayments: (paymentState.data as any)?.recurringPayments ?? [],

      // Last payment (from useLazyGetResidentTransactionInfoQuery)
      lastPaymentInfo: lastPayment
        ? {
            date: lastPayment.transactionDate,
            amount: Number(lastPayment.amount ?? lastPayment.paymentLedgerAmount ?? 0),
            settlementStatus: lastPayment.paymentSettlementStatus,
          }
        : null,

      // Related accounts
      linkedAccounts: d.linkedAccounts ?? [],
      // API returns `{ roommatesCurrent, roommatesFuture }`; flatten to an array.
      roommates: (() => {
        const r = (d as any).roommates;
        if (Array.isArray(r)) return r;
        if (r && typeof r === 'object') {
          return [...(r.roommatesCurrent ?? []), ...(r.roommatesFuture ?? [])];
        }
        return [];
      })(),

      // Derived
      hasRecurringPayments: (paymentState.data as any)?.hasAutopay,
    } as IResidentDetailsResponse;
  }, [detailsState.data, paymentState.data, txState.data, leaseId]);

  // Manual retry function
  const refetch = useCallback(() => {
    if (isMounted.current) {
      setEnhancedError(null);
      fetchResidentDetails();
    }
  }, [fetchResidentDetails]);

  const isLoading =
    detailsState.isLoading ||
    detailsState.isUninitialized ||
    detailsState.isFetching ||
    paymentState.isFetching ||
    txState.isFetching;

  return {
    residentDetails,
    isLoading,
    isError: detailsState.isError,
    error: detailsState.error,
    enhancedError,
    refetch,
    retryCount,
    canRetry: enhancedError?.shouldRetry || false,
  };
};
